import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Security } from '@okta/okta-react';
import OktaAuth, { toRelativeUrl } from '@okta/okta-auth-js';
import oktaConfig from '../oktaConfig';

import RouterWrapper from './RouterWrapper';
import { useTranslation } from 'react-i18next';
import { imposeFrenchLocale } from '../pages/ReactHome/ReactHome';
import { KeycloakProvider } from '../contexts/KeycloakContext';
import { getAuthMode, AUTH_MODES } from '../utils/authMode';
import { isKeycloakAllowedLocation } from '../utils/keycloakLocation';

/**
 * Initialize Okta Authentication (Lazy)
 * 
 * OktaAuth instance is created lazily only when authMode is 'okta'.
 * This prevents unnecessary initialization when using Keycloak.
 */
let oktaAuth = null;

function getOktaAuth() {
    if (!oktaAuth) {
        // Test network connectivity to Okta server
        const oktaDomain = new URL(oktaConfig.issuer).hostname;
        fetch(`https://${oktaDomain}/.well-known/openid-configuration`, { 
            method: 'GET',
            mode: 'cors',
            cache: 'no-cache'
        })
        .then(response => {
            // Okta server is reachable
        })
        .catch(error => {
            // Cannot reach Okta server - authentication may fail
        });

        oktaAuth = new OktaAuth(oktaConfig);
    }
    return oktaAuth;
}

function setupOktaInterceptors() {
    const oktaAuth = getOktaAuth();
    
    // Skip setup if oktaAuth is not properly initialized (e.g., in test environment)
    if (!oktaAuth || typeof oktaAuth.signInWithRedirect !== 'function') {
        return;
    }
    
    // Detect if running in automated browser (Selenium/WebDriver)
    const isAutomatedBrowser = !!(
        navigator.webdriver || 
        window.navigator.webdriver ||
        window.navigator.automation ||
        window.document.__selenium_unwrapped ||
        window.document.__webdriver_evaluate ||
        window.document.__driver_evaluate
    );

    // Detect automated browsers - may need special handling for Okta redirects

    // Wrap signInWithRedirect to add comprehensive logging and retry logic
    const originalSignInWithRedirect = oktaAuth.signInWithRedirect.bind(oktaAuth);
    let redirectAttempts = 0;
    const MAX_REDIRECT_ATTEMPTS = 3;

    oktaAuth.signInWithRedirect = function(...args) {
        redirectAttempts++;
        
        // WORKAROUND: In automated browsers, add extra stabilization time
        if (isAutomatedBrowser) {
            return new Promise((resolve, reject) => {
                setTimeout(() => {
                    const startUrl = window.location.href;
                    
                    originalSignInWithRedirect(...args)
                        .then((result) => {
                            // Monitor if redirect actually happened
                            setTimeout(() => {
                                const currentUrl = window.location.href;
                                const redirectFailed = (
                                    currentUrl === startUrl || 
                                    currentUrl.includes('chrome-error://') ||
                                    currentUrl.includes('data:text/html,chromewebdata')
                                );
                                
                                if (redirectFailed && redirectAttempts < MAX_REDIRECT_ATTEMPTS) {
                                    setTimeout(() => {
                                        oktaAuth.signInWithRedirect(...args);
                                    }, 2000);
                                } else if (redirectFailed && isKeycloakAllowedLocation()) {
                                    // Only redirect to Keycloak if we're at an allowed location
                                    window.location.href = window.location.origin + '/?keycloak=true';
                                }
                            }, 2000); // Check 2 seconds after redirect resolves
                            
                            resolve(result);
                        })
                        .catch(reject);
                }, 1000);
            });
        }
    
        try {
            const promise = originalSignInWithRedirect(...args);
            
            if (promise && promise.then) {
                return promise
                    .then((result) => {
                        // Set a timeout to check if we're still on the same page
                        setTimeout(() => {
                            const currentUrlAfterDelay = window.location.href;
                            
                            // WORKAROUND: Detect if we're still on login page (redirect failed)
                            if (isAutomatedBrowser && currentUrlAfterDelay.includes('/login') && isKeycloakAllowedLocation()) {
                                // Optionally show user a message - only if Keycloak is available at this location
                                if (window.confirm('Authentication redirect failed. This is a known issue with automated browsers.\n\nWould you like to switch to Keycloak authentication mode?\n(More reliable for automated testing)')) {
                                    window.location.href = '/?keycloak=true';
                                }
                            }
                        }, 500);
                        
                        return result;
                    })
                    .catch((error) => {
                        // Show user-friendly error
                        alert(`Cannot connect to Okta authentication server.\n\nError: ${error.message}\n\nPlease check your network connection or try again later.`);
                        
                        throw error;
                    });
            }
            
            return promise;
        } catch (error) {
            alert(`Authentication error: ${error.message}`);
            throw error;
        }
    };

    // Intercept all fetch requests to log Okta API calls
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
        const url = args[0];
        const urlString = typeof url === 'string' ? url : url?.url || 'unknown';
        
        if (urlString.includes('eaton.com') || urlString.includes('okta')) {
            return originalFetch.apply(this, args)
                .then(response => {
                    return response;
                })
                .catch(error => {
                    throw error;
                });
        }
        
        return originalFetch.apply(this, args);
    };

    // WORKAROUND: Detect if we're on Chrome error page after failed Okta redirect
    if (isAutomatedBrowser && isKeycloakAllowedLocation()) {
        // Check periodically if we ended up on error page
        // Only redirect to Keycloak if we're at an allowed location
        const checkForErrorPage = setInterval(() => {
            const url = window.location.href;
            
            if (url.includes('chrome-error://') || url.includes('data:text/html,chromewebdata')) {
                clearInterval(checkForErrorPage);
                
                // Navigate back with Keycloak mode
                setTimeout(() => {
                    window.location.href = window.location.origin + '/?keycloak=true';
                }, 1000);
            }
            
            // Stop checking after 30 seconds
            setTimeout(() => clearInterval(checkForErrorPage), 30000);
        }, 500);
    }

    // Add global error handler to catch network issues during authentication
    window.addEventListener('unhandledrejection', (event) => {
        if (event.reason && (
            event.reason.message?.includes('okta') || 
            event.reason.message?.includes('authorize') ||
        event.reason.message?.includes('Failed to fetch')
    )) {
        // Network issue during Okta authentication
    }
    });
}

/**
 * AppRouter Component
 * 
 * Top-level authentication provider wrapper for the BUC Portal application.
 * 
 * PURPOSE:
 * This component implements the dual authentication system by conditionally
 * rendering either the Okta provider or Keycloak provider based on the
 * selected authentication mode.
 * 
 * DUAL AUTHENTICATION ARCHITECTURE:
 * 
 * ┌─────────────────────────────────────────────────────────────┐
 * │                        AppRouter                             │
 * │                                                              │
 * │  authMode State: 'okta' | 'keycloak'                        │
 * │  (stored in localStorage, default: 'okta')                  │
 * │                                                              │
 * │  ┌─────────────────────────────────────────────────────┐   │
 * │  │  If authMode === 'okta':                            │   │
 * │  │    <Security oktaAuth={oktaAuth}>                   │   │
 * │  │      <RouterWrapper authMode="okta" />              │   │
 * │  │    </Security>                                      │   │
 * │  └─────────────────────────────────────────────────────┘   │
 * │                                                              │
 * │  ┌─────────────────────────────────────────────────────┐   │
 * │  │  If authMode === 'keycloak':                        │   │
 * │  │    <KeycloakProvider>                               │   │
 * │  │      <RouterWrapper authMode="keycloak" />          │   │
 * │  │    </KeycloakProvider>                              │   │
 * │  └─────────────────────────────────────────────────────┘   │
 * └─────────────────────────────────────────────────────────────┘
 * 
 * HOW AUTHENTICATION MODE IS DETERMINED:
 * 1. Default: Okta mode (no URL parameter needed)
 * 2. Keycloak mode: User adds ?keycloak=true to URL at allowed locations
 *    - LOCATION RESTRICTIONS: Keycloak only activates at:
 *      • localhost:8415 (development)
 *      • smpservices-dev.eastus.cloudapp.azure.com (development Azure)
 *    - ReactHome component detects parameter and validates location
 *    - Calls setAuthMode(AUTH_MODES.KEYCLOAK)
 *    - Triggers page reload to initialize KeycloakProvider
 *    - At other locations, keycloak parameter is ignored
 * 
 * WHY PAGE RELOAD IS REQUIRED:
 * When switching authentication modes, the provider wrapper must be
 * completely re-initialized. A page reload ensures clean provider state
 * and avoids React context conflicts.
 * 
 * STORAGE EVENT LISTENER:
 * The storage event listener allows auth mode changes to propagate across
 * browser tabs (if needed in future), though current implementation primarily
 * uses localStorage within the same tab.
 * 
 * @param {Object} prop - Component props
 * @param {React.Component} prop.SharedLayoutWrapper - Layout wrapper for authenticated routes
 */
export const AppRouter = (prop) => {
    const { SharedLayoutWrapper } = prop;
    const navigation = useNavigate();
    const { i18n } = useTranslation();
    
    /**
     * Authentication Mode State
     * 
     * Tracks which authentication provider is currently active.
     * Initialized from localStorage (defaults to OKTA if not set).
     * 
     * This state drives the conditional rendering of provider wrappers below.
     */
    const initialAuthMode = getAuthMode();
    const [authMode, setAuthModeState] = useState(initialAuthMode);

    // Apply French locale if browser language is French
    useEffect(() => {
        imposeFrenchLocale(i18n);
    }, [i18n]);

    // Navigation callback wrapper (required by @brightlayer-ui/react-auth-workflow)
    const navigate = useCallback(
        (destination) => {
            navigation(destination);
        },
        [navigation]
    );

    /**
     * Okta Original URI Restoration
     * 
     * After Okta authentication, restore the user to their originally
     * requested URL (or default to '/' if none specified).
     * 
     * This is called by Okta's Security component after successful login.
     */
    const restoreOriginalUri = (_oktaAuth, originalUri) => {
        navigate(toRelativeUrl(originalUri || '/', window.location.origin));
    };

    /**
     * Listen for Auth Mode Changes
     * 
     * Responds to localStorage changes for the 'authMode' key.
     * This allows auth mode switches to be detected across browser tabs
     * (though current implementation primarily uses single-tab flow).
     * 
     * When authMode changes:
     * 1. Update local state (triggers re-render)
     * 2. Component re-renders with new provider wrapper
     */
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'authMode') {
                const newMode = e.newValue || AUTH_MODES.OKTA;
                setAuthModeState(newMode);
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    /**
     * KEYCLOAK MODE RENDERING
     * 
     * When authMode is 'keycloak', wrap the app with KeycloakProvider.
     * This provider:
     * - Initializes the Keycloak JS adapter
     * - Manages Keycloak authentication state
     * - Provides Keycloak context to child components
     * - Handles email-first authentication flow
     * - Manages token refresh
     * 
     * The authMode prop is passed to RouterWrapper so it knows to:
     * - Use Keycloak hooks and state
     * - Render Keycloak-specific components
     * - Handle Keycloak logout flow
     * 
     * NOTE: KeycloakProvider must be rendered inside BrowserRouter context
     * as some child components may use React Router hooks
     */
    if (authMode === AUTH_MODES.KEYCLOAK) {
        return (
            <KeycloakProvider>
                <RouterWrapper SharedLayoutWrapper={SharedLayoutWrapper} authMode={authMode} />
            </KeycloakProvider>
        );
    }

    /**
     * OKTA MODE RENDERING (DEFAULT)
     * 
     * When authMode is 'okta' (or undefined/null), wrap the app with
     * Okta's <Security> component.
     * 
     * Initialize OktaAuth instance and interceptors lazily (only when using Okta).
     * 
     * This provider:
     * - Initializes Okta authentication state
     * - Provides useOktaAuth() hook to child components
     * - Handles OAuth2/OIDC token management
     * - Manages automatic token refresh
     * - Restores original URI after authentication
     * 
     * The authMode prop is passed to RouterWrapper so it knows to:
     * - Use Okta hooks and state
     * - Render Okta-specific components (OktaRedirectLoginScreen)
     * - Handle Okta logout flow with signOut()
     * 
     * NOTE: Security component must be rendered inside BrowserRouter context
     * as it may internally use React Router features and child components
     * like ReactRouterGuestGuard require Router context
     */
    // Lazy initialization: only create OktaAuth instance when needed
    const oktaAuthInstance = getOktaAuth();
    setupOktaInterceptors();
    
    return (
        <Security oktaAuth={oktaAuthInstance} restoreOriginalUri={restoreOriginalUri}>
            <RouterWrapper SharedLayoutWrapper={SharedLayoutWrapper} authMode={authMode} />
        </Security>
    );
};
